<template>
  <div class="w-full px-6 py-4">
    <div class="md:flex space-x-4 mb-6">
      <o-field-group
        v-slot="{ status }"
        rules="required"
        :validation-value="_pass.name"
        class="w-full"
        :label="$t('general.name')"
        :info="$t('components.pass.edit.details.info')"
      >
        <text-field v-model="_pass.name" :label="$t('general.name')" :status="status" />
      </o-field-group>
    </div>

    <div class="md:flex space-x-4 mb-6">
      <o-field-group
        v-slot="{ status }"
        rules="required"
        :validation-value="_pass.information"
        class="w-full"
        :label="$t('general.info')"
        :info="$t('components.pass.edit.details.infoInfo')"
      >
        <text-area-field
          v-model="_pass.information"
          rows="3"
          :status="status"
          :maxlength="1000"
          counter
        />
      </o-field-group>
    </div>

    <div class="md:flex space-x-4 mb-6">
      <o-field-group
        v-slot="{ status }"
        rules="required"
        :validation-value="_pass.instructions"
        class="w-full"
        :label="$t('general.instructions')"
        :info="$t('components.pass.edit.details.instructionsInfo')"
      >
        <text-area-field
          v-model="_pass.instructions"
          rows="3"
          :status="status"
          :maxlength="1000"
          counter
        />
      </o-field-group>
    </div>

    <div v-if="!_pass.isNewRecord" class="md:flex space-x-4 mb-6">
      <o-field-group :label="$t('general.image')" :info="$t('product.visibleInLists')">
        <avatar-field :model="_pass" @click="handleUpdateImage" />
      </o-field-group>
    </div>

    <div class="md:flex space-x-4 mb-6">
      <o-field-group
        v-slot="{ status }"
        rules="required"
        :validation-value="_pass.price"
        class="w-full md:w-1/2"
        :label="$t('general.price')"
        :info="$t('components.pass.edit.details.priceInfo')"
      >
        <currency-field v-model="_pass.price" :status="status" />
      </o-field-group>

      <o-field-group
        v-slot="{ status }"
        rules="required"
        :validation-value="_pass.onlineLimitPerCustomer"
        :message="$t('components.pass.edit.details.limitMessage')"
        class="w-full md:w-1/2"
        :label="$t('components.pass.edit.details.limit')"
        :info="$t('components.pass.edit.details.limitInfo')"
      >
        <number-field v-model="_pass.onlineLimitPerCustomer" :status="status" />
      </o-field-group>
    </div>

    <div class="md:flex space-x-4 mb-4">
      <o-field-group
        col
        reverse
        class="w-full md:w-1/2"
        :label="$t('components.pass.edit.details.allowOnlinePurchase')"
        :info="$t('components.pass.edit.details.allowOnlinePurchaseInfo')"
      >
        <toggle-field v-model="_pass.allowInternet" />
      </o-field-group>

      <o-field-group
        col
        reverse
        class="w-full md:w-1/2"
        :label="$t('components.pass.edit.details.allowGifting')"
        :info="$t('components.pass.edit.details.allowGiftingInfo')"
      >
        <toggle-field v-model="_pass.allowGifting" />
      </o-field-group>
    </div>

    <div class="md:flex space-x-4 mb-7">
      <o-field-group
        v-if="$features.get('loyalty')"
        col
        reverse
        class="w-full md:w-1/2"
        :label="$t('general.loyaltyEnabled')"
        :info="$t('components.pass.edit.details.loyaltyEnabledInfo')"
      >
        <toggle-field v-model="_pass.loyaltyEnabled" />
      </o-field-group>

      <o-field-group
        col
        reverse
        class="w-full md:w-1/2"
        :label="$t('components.pass.edit.details.notifyCustomer')"
        :info="$t('components.pass.edit.details.notifyCustomerInfo')"
      >
        <toggle-field v-model="_pass.expiredNotificationEnabled" />
      </o-field-group>
    </div>

    <div class="md:flex space-x-4 mb-10">
      <o-field-group
        class="w-full md:w-1/2"
        :label="$t('components.pass.edit.details.applyPass')"
      >
        <select-field
          v-model="_pass.preselectAtConfirmationDefault"
          :options="[
            { value: true, label: $t('general.confirmation') },
            { value: false, label: $t('general.checkIn') }
          ]"
        />
      </o-field-group>
    </div>

    <div class="bg-gray-50 border rounded-md p-3 mb-6 dark:bg-gray-900">
      <div class="flex items-center justify-between class border-b pb-2 mb-6">
        <div>
          <h5 class="text-gray-700 font-medium dark:text-gray-200">
            {{ $t('general.expires') }}
          </h5>
          <p class="text-sm text-gray-600 dark:text-gray-300">
            {{ $t('components.pass.edit.details.includesExpiry') }}
          </p>
        </div>

        <toggle-field v-model="_pass.expires" />
      </div>

      <div>
        <div class="md:flex space-x-4 mb-6">
          <o-field-group
            v-slot="{ status }"
            :rules="_pass.expires ? 'required|min_value:1' : ''"
            :validation-value="_pass.expiryDays"
            class="w-full md:w-1/2"
            :label="$t('general.expiryDays')"
            :name="$t('general.expiryDays')"
            :info="$t('components.pass.edit.details.expiryDaysInfo')"
          >
            <number-field
              v-model="_pass.expiryDays"
              :status="status"
              :disabled="!_pass.expires"
            />
          </o-field-group>

          <o-field-group
            class="w-full md:w-1/2"
            :label="$t('components.pass.edit.details.expiryMode')"
            :info="$t('components.pass.edit.details.expiryModeInfo')"
          >
            <select-field
              v-model="_pass.expiryMode"
              :disabled="!_pass.expires"
              :options="expiryModeOptions"
            />
          </o-field-group>
        </div>

        <div class="md:flex space-x-4 mb-4">
          <o-field-group
            class="w-full md:w-1/2"
            :label="$t('components.pass.edit.details.daysRemainingReminder')"
            :info="$t('components.pass.edit.details.daysRemainingReminderInfo')"
          >
            <number-field
              v-model="_pass.daysRemainingReminder"
              :disabled="!_pass.expires"
            />
          </o-field-group>
        </div>
      </div>
    </div>

    <div class="bg-gray-50 border rounded-md p-3 mb-10 dark:bg-gray-900">
      <div class="flex items-center justify-between class border-b pb-2 mb-6">
        <div>
          <h5 class="text-gray-700 font-medium dark:text-gray-200">
            {{ $t('components.pass.edit.details.limitedUse') }}
          </h5>
          <p class="text-sm text-gray-600 dark:text-gray-300">
            {{ $t('components.pass.edit.details.limitedUseInfo') }}
          </p>
        </div>

        <toggle-field v-model="_pass.isLimitedUse" />
      </div>

      <div>
        <div class="md:flex space-x-4 mb-6">
          <o-field-group
            v-slot="{ status }"
            :rules="_pass.isLimitedUse ? 'required|min_value:1' : ''"
            :validation-value="_pass.numberOfUses"
            class="w-full md:w-1/2"
            :label="$t('components.pass.edit.details.numberOfUses')"
            :name="$t('components.pass.edit.details.numberOfUses')"
            :info="$t('components.pass.edit.details.numberOfUsesInfo')"
          >
            <number-field
              v-model="_pass.numberOfUses"
              :status="status"
              :disabled="!_pass.isLimitedUse"
            />
          </o-field-group>

          <o-field-group
            class="w-full md:w-1/2"
            :label="$t('components.pass.edit.details.usesRemainingReminder')"
            :info="$t('components.pass.edit.details.usesReminaingReminderInfo')"
          >
            <number-field
              v-model="_pass.usesRemainingReminder"
              :disabled="!_pass.isLimitedUse"
            />
          </o-field-group>
        </div>
      </div>
    </div>

    <div class="md:flex space-x-4 mb-6">
      <o-field-group
        class="w-full"
        :label="$t('components.pass.edit.details.cancelAppointmentBehaviour')"
        :info="$t('components.pass.edit.details.cancelAppointmentBehaviourInfo')"
      >
        <select-field
          v-model="_pass.cancelReservationBehaviour"
          :options="cancelReservationBehaviourOptions"
        />
      </o-field-group>

      <o-field-group
        class="w-full"
        :label="$t('components.pass.edit.details.deleteAppointmentBehaviour')"
        :info="$t('components.pass.edit.details.deleteAppointmentBehaviourInfo')"
      >
        <select-field
          v-model="_pass.deleteReservationBehaviour"
          :options="deleteReservationBehaviourOptions"
        />
      </o-field-group>
    </div>

    <update-image-dialog
      ref="updateImageDialog"
      :pass="_pass"
      @update="handleUpdateImageSuccess"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { OAvatar } from '@/components/elements'

import UpdateImageDialog from '@/components/pass/actions/Image.vue'

import {
  TextField,
  CurrencyField,
  SelectField,
  NumberField,
  TextAreaField,
  ToggleField,
  AvatarField,
} from '@/components/fields'

import {
  PassExpiryMode,
  PassExpiryModeLabels,
  PassCancelReservationBehaviour,
  PassCancelReservationBehaviourLabels,
  PassDeleteReservationBehaviour,
  PassDeleteReservationBehaviourLabels,
} from '~/_api/models/pass'

interface Props {
  pass: Record<string, any>
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:pass', value: Record<string, any>): void
}>()

const { t } = useI18n()
const { $bus } = useNuxtApp()

const updateImageDialog = ref<any>()

const _pass = computed({
  get () {
    return props.pass
  },
  set (value) {
    emit('update:pass', value)
  },
})

const cancelReservationBehaviourOptions = computed(() => {
  return Object.values(PassCancelReservationBehaviour)
    .filter((v): v is PassCancelReservationBehaviour => typeof v === 'number')
    .map((value) => ({
      label: t(PassCancelReservationBehaviourLabels[value]),
      value,
    }))
})

const deleteReservationBehaviourOptions = computed(() => {
  return Object.values(PassDeleteReservationBehaviour)
    .filter((v): v is PassDeleteReservationBehaviour => typeof v === 'number')
    .map((value) => ({
      label: t(PassDeleteReservationBehaviourLabels[value]),
      value,
    }))
})

const expiryModeOptions = computed(() => {
  return Object.values(PassExpiryMode)
    .filter((v): v is PassExpiryMode => typeof v === 'number')
    .map((value) => ({
      label: t(PassExpiryModeLabels[value]),
      value,
    }))
})

function handleUpdateImage () {
  updateImageDialog.value.open()
}

function handleUpdateImageSuccess (pass: any) {
  _pass.value.images = pass.images
  _pass.value.hasImage = !!pass.images
  $bus.emit.updated('Pass')
}
</script>
